import React, { useEffect, useMemo } from "react";
import { Checkbox, DatePicker, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import "./modal.scss";
import NeoModalDialog from "../../../../common/components/neo-modal-dialog";
import ImpactXButton from "../../../../common/components/impactx-button";
import Progress from "../../../../common/components/progress";
import {
  CONFIRMATION_HINT,
  CONFIRMATION_TEXT,
  FieldConfig,
  REVIEW_REQUEST_FORM_SCHEMAS,
} from "./form-config";
import { ReviewRequestFormSubmission, ReviewRequestType } from "./types";

const { TextArea } = Input;

export interface ReviewRequestModalProps {
  show: boolean;
  reviewType: ReviewRequestType | null;
  defaultPrimaryContact?: string;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit: (submission: ReviewRequestFormSubmission) => void;
}

const DATE_FORMAT = "YYYY-MM-DD";
const REVIEW_TYPE_FIELD = "reviewType";
const CONFIRMATION_FIELD = "confirmation";

const ReviewRequestModal: React.FC<ReviewRequestModalProps> = ({
  show,
  reviewType,
  defaultPrimaryContact,
  isSubmitting,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();

  const schema = useMemo(
    () => (reviewType ? REVIEW_REQUEST_FORM_SCHEMAS[reviewType] : null),
    [reviewType],
  );

  useEffect(() => {
    if (show && schema) {
      form.resetFields();
      form.setFieldsValue({
        [REVIEW_TYPE_FIELD]: schema.reviewTypeLabel,
        primaryContact: defaultPrimaryContact ?? "",
        [CONFIRMATION_FIELD]: false,
      });
    }
  }, [show, schema, defaultPrimaryContact, form]);

  if (!reviewType || !schema) {
    return null;
  }

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const normalizedValues: Record<string, unknown> = {};
      Object.keys(values).forEach((key) => {
        if (key === REVIEW_TYPE_FIELD) {
          return;
        }
        const raw = values[key];
        normalizedValues[key]
          = dayjs.isDayjs(raw) ? raw.format(DATE_FORMAT) : raw;
      });
      onSubmit({ reviewType, values: normalizedValues });
    }
    catch {
      // antd surfaces validation errors inline
    }
  };

  const renderField = (field: FieldConfig) => {
    const baseRules = field.required
      ? [{ required: true, message: field.requiredMessage ?? "Required" }]
      : [];

    const lengthRules = [
      ...(field.minLength !== undefined
        ? [{ min: field.minLength, message: field.minLengthMessage ?? `Minimum ${field.minLength} characters required` }]
        : []),
      ...(field.maxLength !== undefined
        ? [{ max: field.maxLength, message: field.maxLengthMessage ?? `Maximum ${field.maxLength} characters allowed` }]
        : []),
    ];

    switch (field.kind) {
    case "text":
      return (
        <Form.Item
          key={field.name}
          name={field.name}
          label={field.label}
          rules={[...baseRules, ...lengthRules]}
          extra={field.helperText}
        >
          <Input disabled={field.disabled} />
        </Form.Item>
      );
    case "email":
      return (
        <Form.Item
          key={field.name}
          name={field.name}
          label={field.label}
          rules={[
            ...baseRules,
            { type: "email", message: field.invalidMessage ?? "Enter a valid email address" },
          ]}
          extra={field.helperText}
        >
          <Input disabled={field.disabled} />
        </Form.Item>
      );
    case "url":
      return (
        <Form.Item
          key={field.name}
          name={field.name}
          label={field.label}
          rules={[
            ...baseRules,
            { type: "url", message: field.invalidMessage ?? "Enter a valid URL" },
          ]}
          extra={field.helperText}
        >
          <Input disabled={field.disabled} />
        </Form.Item>
      );
    case "textarea":
      return (
        <Form.Item
          key={field.name}
          name={field.name}
          label={field.label}
          rules={[...baseRules, ...lengthRules]}
          extra={field.helperText}
        >
          <TextArea
            rows={field.rows ?? 4}
            disabled={field.disabled}
          />
        </Form.Item>
      );
    case "date":
      return (
        <Form.Item
          key={field.name}
          name={field.name}
          label={field.label}
          rules={baseRules}
          extra={field.helperText}
        >
          <DatePicker
            format={DATE_FORMAT}
            style={{ width: "100%" }}
            disabled={field.disabled}
            disabledDate={(current) => current ? !current.isAfter(dayjs().startOf("day")) : false}
          />
        </Form.Item>
      );
    case "select":
      return (
        <Form.Item
          key={field.name}
          name={field.name}
          label={field.label}
          rules={baseRules}
          extra={field.helperText}
        >
          <Select
            options={field.options ?? []}
            disabled={field.disabled}
          />
        </Form.Item>
      );
    default:
      return null;
    }
  };

  const renderConfirmation = () => {
    if (!schema.showConfirmation) {
      return null;
    }
    return (
      <Form.Item
        className="review-request-confirmation"
        name={CONFIRMATION_FIELD}
        valuePropName="checked"
        rules={[
          {
            validator: (_rule, value: boolean) =>
              value
                ? Promise.resolve()
                : Promise.reject(new Error("Please confirm before submitting")),
          },
        ]}
      >
        <Checkbox>
          <span className="review-request-confirmation-text">
            {CONFIRMATION_TEXT}
            {" "}
            <em>{CONFIRMATION_HINT}</em>
          </span>
        </Checkbox>
      </Form.Item>
    );
  };

  const renderFooter = () => (
    <div className="review-request-footer">
      <ImpactXButton
        outline={true}
        label="Cancel"
        onClickHandler={handleCancel}
        disabled={isSubmitting}
      />
      <ImpactXButton
        label="Submit for Review"
        onClickHandler={() => { void handleSubmit(); }}
        disabled={isSubmitting}
      />
    </div>
  );

  return (
    <NeoModalDialog
      dialogTitle="Review Request"
      dialogClassName="ReviewRequestModal"
      modalSize="lg"
      show={show}
      onHide={handleCancel}
      footerContent={renderFooter()}
    >
      {isSubmitting && (
        <div className="review-request-loading-overlay">
          <Progress text="Submitting..." />
        </div>
      )}
      <Form
        form={form}
        layout="vertical"
        className="review-request-form"
        requiredMark={(label, info) => (
          <>
            {label}
            {info.required ? <span className="required-asterisk"> *</span> : null}
          </>
        )}
      >
        <Form.Item
          name={REVIEW_TYPE_FIELD}
          label="Review Type"
        >
          <Input disabled />
        </Form.Item>
        {schema.fields.map(renderField)}
        {renderConfirmation()}
      </Form>
    </NeoModalDialog>
  );
};

export default ReviewRequestModal;
